import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useFinanceTable } from './useFinanceTable'

// ---------- Specialised hooks ----------

// Latest balance entry is the source of truth. The list of entries
// (sorted descending) doubles as the balance-history feed in Phase 5.
export function useBalance() {
  const { user } = useAuth()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('balance_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('as_of_date', { ascending: false })
      .order('created_at', { ascending: false })
    if (err) setError(err.message)
    else setEntries(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const addEntry = async ({ amount, as_of_date, notes }) => {
    if (!user) return null
    const { error: err } = await supabase.from('balance_entries').insert({
      user_id: user.id,
      amount: Number(amount),
      as_of_date: as_of_date || new Date().toLocaleDateString('en-CA'),
      notes: notes || null,
    })
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  const removeEntry = async (id) => {
    const { error: err } = await supabase.from('balance_entries').delete().eq('id', id)
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  const latest = entries[0] || null

  return { entries, latest, loading, error, addEntry, removeEntry, refetch: fetch }
}

// Single-row table (PRIMARY KEY user_id). The "row" is upserted
// rather than created/deleted, which matches how the UI presents it
// (one editable estimate, no list).
export function useVariableExpenses() {
  const { user } = useAuth()
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('variable_expenses')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()
    if (err) setError(err.message)
    else setRow(data || null)
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const save = async ({ monthly_estimate, notes }) => {
    if (!user) return false
    const { error: err } = await supabase.from('variable_expenses').upsert({
      user_id: user.id,
      monthly_estimate: Number(monthly_estimate) || 0,
      notes: notes || null,
      updated_at: new Date().toISOString(),
    })
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  return { row, loading, error, save, refetch: fetch }
}

// ---------- Thin wrappers around the generic CRUD ----------
// Stable filter/orderBy literals (frozen) so the inner useCallback
// in useFinanceTable doesn't re-create the fetch on every render.

const ACTIVE_FILTER = Object.freeze([{ column: 'status', op: 'eq', value: 'active' }])
const NAME_ORDER = Object.freeze({ column: 'name', ascending: true })
// installments has provider/label instead of a single `name` column,
// so ORDER BY name 500s and the supabase-js client returns an empty
// result with the error swallowed by our setError handler — the rows
// are in the DB but invisible to the UI. Order by provider instead;
// keeps the alphabetical-ish grouping the original NAME_ORDER tried to.
const PROVIDER_ORDER = Object.freeze({ column: 'provider', ascending: true })

export function useIncomeSources({ activeOnly = true } = {}) {
  const t = useFinanceTable({
    table: 'income_sources',
    filter: activeOnly ? ACTIVE_FILTER : [],
    orderBy: NAME_ORDER,
  })
  return { ...t, sources: t.rows }
}

export function useFixedExpenses({ activeOnly = true } = {}) {
  const t = useFinanceTable({
    table: 'fixed_expenses',
    filter: activeOnly ? ACTIVE_FILTER : [],
    orderBy: NAME_ORDER,
  })
  return { ...t, expenses: t.rows }
}

export function useInstallments({ activeOnly = true } = {}) {
  const t = useFinanceTable({
    table: 'installments',
    filter: activeOnly ? ACTIVE_FILTER : [],
    orderBy: PROVIDER_ORDER,
  })
  return { ...t, installments: t.rows }
}

// ---------- Aggregated dashboard summary ----------
// Pure derivation off the four hooks above. Memoised so the dashboard
// can render off it without re-summing on every keypress.
export function useFinanceSummary({
  balance,
  incomeSources,
  fixedExpenses,
  installments,
  variableExpenses,
  specialObligations = [],
}) {
  return useMemo(() => {
    const sum = (arr, key) =>
      arr.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)

    const monthlyIncome = sum(
      incomeSources.filter((s) => s.frequency === 'monthly'),
      'amount',
    )
    const monthlyFixed = sum(fixedExpenses, 'amount')
    const monthlyInstallments = sum(
      installments.filter((i) => (i.remaining_months ?? 0) > 0),
      'monthly_amount',
    )
    const monthlyVariable = Number(variableExpenses?.monthly_estimate) || 0

    // Special obligations that fall in *this* month. Monthly ones
    // (jam3eya, swim, monthly customs) fire if today is between
    // start_date and end_date inclusive. Every-6-month rows
    // (rent) fire only when their attribution_month matches the
    // current YYYY-MM. one_time fires the same way.
    const now = new Date()
    const todayIso = now.toLocaleDateString('en-CA')
    const thisMonthKey = todayIso.slice(0, 7)
    const monthlyObligations = specialObligations.reduce((acc, o) => {
      if (o.status !== 'active') return acc
      const amount = Number(o.amount) || 0
      if (o.frequency === 'monthly') {
        const inRange =
          (!o.start_date || o.start_date <= todayIso) &&
          (!o.end_date || o.end_date >= todayIso)
        return inRange ? acc + amount : acc
      }
      if (o.frequency === 'every_6_months' || o.frequency === 'one_time') {
        return o.attribution_month === thisMonthKey ? acc + amount : acc
      }
      return acc
    }, 0)

    const monthlyOutflow = monthlyFixed + monthlyInstallments + monthlyVariable + monthlyObligations
    const monthlyNet = monthlyIncome - monthlyOutflow

    const currentBalance = Number(balance?.amount) || 0
    const expectedEndOfMonth = currentBalance + monthlyNet

    const risk = riskFor(expectedEndOfMonth)

    return {
      currentBalance,
      monthlyIncome,
      monthlyFixed,
      monthlyInstallments,
      monthlyVariable,
      monthlyObligations,
      monthlyOutflow,
      monthlyNet,
      expectedEndOfMonth,
      risk,
    }
  }, [balance, incomeSources, fixedExpenses, installments, variableExpenses, specialObligations])
}

// PRD section 5.1: 🟢 > 5k, 🟡 1k–5k, 🔴 < 1k.
function riskFor(amount) {
  if (amount < 1000) return 'critical'
  if (amount < 5000) return 'warning'
  return 'safe'
}

// ---------- Special obligations + financial targets ----------
// Thin wrappers (same shape as the income/fixed/installments ones)
// against the two tables that landed in migration 019 but had no UI
// in Phase 1.

const ACTIVE_OR_INPROGRESS_FILTER = Object.freeze([
  { column: 'status', op: 'neq', value: 'achieved' },
])
const CREATED_DESC_ORDER = Object.freeze({ column: 'created_at', ascending: false })

export function useSpecialObligations({ activeOnly = true } = {}) {
  const t = useFinanceTable({
    table: 'special_obligations',
    filter: activeOnly ? ACTIVE_FILTER : [],
    orderBy: CREATED_DESC_ORDER,
  })
  return { ...t, obligations: t.rows }
}

// Targets have four legal statuses; the working list excludes only
// 'achieved' rows (which live in the achievements card). 'missed'
// rows still want eyes on them so they stay in the active list with
// a different badge.
export function useFinancialTargets({ allStatuses = false } = {}) {
  const t = useFinanceTable({
    table: 'financial_targets',
    filter: allStatuses ? [] : ACTIVE_OR_INPROGRESS_FILTER,
    orderBy: CREATED_DESC_ORDER,
  })
  return { ...t, targets: t.rows }
}
